import React, { useState, useEffect, useRef } from 'react';
import { Save, Play, Brain, Code, User, MessageCircle, Smile } from 'lucide-react';
import styled, { useTheme } from 'styled-components';

// Mapa de nomes formatados para exibição
const SCRIPT_LABELS: Record<string, string> = {
  conteudo: 'Conteúdo',
  mentalidade: 'Mentalidade',
  fatos_produto: 'Fatos do Produto',
  persona: 'Persona',
  girias: 'Gírias',
  abreviacoes: 'Abreviações',
  emojis: 'Emojis',
  preco: 'Preço',
  beneficios: 'Benefícios',
  objecoes: 'Objeções',
  storytelling: 'Storytelling',
};

const formatScriptName = (name: string): string => {
  const lowerName = name.toLowerCase().replace(/[_-]/g, '_');
  if (SCRIPT_LABELS[lowerName]) {
    return SCRIPT_LABELS[lowerName];
  }
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const EditorContainer = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  overflow: hidden;
  height: 100%;
  min-height: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const EditorHeader = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EditorTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const EditorIcon = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: ${props => {
    switch (props.$type) {
      case 'conteudo': return 'linear-gradient(135deg, #6b7280, #4b5563)';
      case 'mentalidade': return 'linear-gradient(135deg, #ef4444, #dc2626)';
      case 'fatos_produto': return 'linear-gradient(135deg, #f59e0b, #d97706)';
      case 'persona': return 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
      case 'linguagem': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'girias': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'abreviacoes': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'emojis': return 'linear-gradient(135deg, #10b981, #059669)';
      default: return props.theme.colors.backgroundTertiary;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  will-change: transform;
  transform: translateZ(0);

  ${props => {
    const animations = {
      conteudo: 'none',
      mentalidade: 'pulse 2s ease-in-out infinite',
      fatos_produto: 'scale 0.8s ease-in-out',
      persona: 'pulse 2s ease-in-out infinite',
      linguagem: 'none',
      girias: 'none',
      abreviacoes: 'none',
      emojis: 'scale 0.8s ease-in-out'
    };
    return `animation: ${animations[props.$type as keyof typeof animations] || 'none'};`;
  }}

  @keyframes pulse {
    0%, 100% { transform: scale(1) translateZ(0); }
    50% { transform: scale(1.05) translateZ(0); }
  }

  @keyframes rotate {
    from { transform: rotate(0deg) translateZ(0); }
    to { transform: rotate(360deg) translateZ(0); }
  }

  @keyframes scale {
    0%, 100% { transform: scale(1) translateZ(0); }
    50% { transform: scale(1.1) translateZ(0); }
  }
`;

const EditorInfo = styled.div`
  flex: 1;
`;

const EditorName = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const EditorType = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: capitalize;
`;

const EditorActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' | 'warning' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};
  border: 1px solid transparent;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: ${props.theme.colors.primary};
          color: ${props.theme.colors.textPrimary};
          &:hover {
            background: ${props.theme.colors.primaryHover};
            transform: translateY(-1px);
          }
        `;
      case 'success':
        return `
          background: ${props.theme.colors.success};
          color: ${props.theme.colors.background};
          &:hover {
            background: ${props.theme.colors.success}dd;
            transform: translateY(-1px);
          }
        `;
      case 'warning':
        return `
          background: ${props.theme.colors.warning};
          color: ${props.theme.colors.background};
          &:hover {
            background: ${props.theme.colors.warning}dd;
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundTertiary};
          color: ${props.theme.colors.textSecondary};
          border-color: ${props.theme.colors.border};
          &:hover {
            background: ${props.theme.colors.backgroundSecondary};
            color: ${props.theme.colors.textPrimary};
            border-color: ${props.theme.colors.primary};
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const EditorContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const EditorTabs = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const EditorTab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${props => props.$active ? props.theme.colors.backgroundSecondary : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.textPrimary : props.theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const EditorArea = styled.div`
  flex: 1;
  position: relative;
  min-height: 340px;
  max-height: 340px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 340px;
  resize: none;
  border: none;
  outline: none;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: 'JetBrains Mono', Consolas, monospace;
  font-size: 14px;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
    border-radius: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 5px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.textTertiary};
  }
`;

const PreviewArea = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  padding: ${({ theme }) => theme.spacing[4]};
  overflow-y: auto;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.easeInOut};
  z-index: 10;
`;

const PreviewContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PreviewTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[4]} 0;
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const PreviewText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: pre-wrap;
`;

const StatusBar = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const LoadingOverlay = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.easeInOut};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${({ theme }) => theme.colors.border};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

interface ScriptEditorProps {
  scriptType: 'conteudo' | 'mentalidade' | 'fatos_produto' | 'persona' | 'linguagem' | 'emojis' | 'girias' | 'abreviacoes';
  scriptName: string;
  content: string;
  onSave: (content: string) => Promise<void>;
  onTest: (content: string) => Promise<string>;
  loading?: boolean;
}

const ScriptEditor: React.FC<ScriptEditorProps> = ({
  scriptType,
  scriptName,
  content,
  onSave,
  onTest,
  loading = false
}) => {
  useTheme();
  const [editorContent, setEditorContent] = useState(content);
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  // Removido estado showPreview pois não é mais utilizado
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState('');


  useEffect(() => {
    setEditorContent(content);
  }, [content]);

  // Handler manual de wheel para garantir scroll no textarea
  useEffect(() => {
    const textarea = textRef.current;
    if (!textarea) return;

    const handleWheel = (e: WheelEvent) => {
      // Previne que o evento se propague e permite scroll nativo
      e.stopPropagation();

      // Scroll manual como fallback
      textarea.scrollTop += e.deltaY;
    };

    textarea.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      textarea.removeEventListener('wheel', handleWheel);
    };
  }, [activeTab]);

  const handleSave = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsSaving(true);
    try {
      await onSave(editorContent);
    } catch (error) {
      console.error('Erro ao salvar script:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsTesting(true);
    try {
      const result = await onTest(editorContent);
      setTestResult(result);
  // setShowPreview removido pois não existe mais preview
      setActiveTab('preview');
    } catch (error) {
      console.error('Erro ao testar script:', error);
      setTestResult('Erro ao testar script');
    } finally {
      setIsTesting(false);
    }
  };

  // Função handleReset removida pois não é mais utilizada


  const getIcon = () => {
    switch (scriptType) {
      case 'conteudo': return Code;
      case 'mentalidade': return Brain;
      case 'fatos_produto': return Brain;
      case 'persona': return User;
      case 'linguagem': return MessageCircle;
      case 'girias': return MessageCircle;
      case 'abreviacoes': return MessageCircle;
      case 'emojis': return Smile;
      default: return Code;
    }
  };

  const getTypeLabel = () => {
    switch (scriptType) {
      case 'conteudo': return 'Script de Conteúdo';
      case 'mentalidade': return 'Mentalidade';
      case 'fatos_produto': return 'Fatos do Produto';
      case 'persona': return 'Configuração de Persona';
      case 'linguagem': return 'Linguagem';
      case 'girias': return 'Escrita';
      case 'abreviacoes': return 'Escrita';
      case 'emojis': return 'Escrita';
      default: return 'Script';
    }
  };

  const Icon = getIcon();

  return (
    <EditorContainer>
      <EditorHeader>
        <EditorTitle>
          <EditorIcon $type={scriptType}>
            <Icon size={20} />
          </EditorIcon>
          <EditorInfo>
            <EditorName>{formatScriptName(scriptName)}</EditorName>
            <EditorType>{getTypeLabel()}</EditorType>
          </EditorInfo>
        </EditorTitle>
        
        <EditorActions>
          
          
          <ActionButton
            $variant="success"
            onClick={handleTest}
            disabled={isTesting || !editorContent.trim()}
          >
            <Play size={16} />
            {isTesting ? 'Testando...' : 'Testar'}
          </ActionButton>
          
          <ActionButton
            $variant="primary"
            onClick={handleSave}
            disabled={isSaving || editorContent === content}
          >
            <Save size={16} />
            {isSaving ? 'Salvando...' : 'Salvar'}
          </ActionButton>
        </EditorActions>
      </EditorHeader>

      <EditorContent>
        <EditorTabs>
          <EditorTab
            $active={activeTab === 'edit'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveTab('edit');
            }}
          >
            Editor
          </EditorTab>
          <EditorTab
            $active={activeTab === 'preview'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveTab('preview');
            }}
          >
            Preview
          </EditorTab>
        </EditorTabs>

        <EditorArea>
          {activeTab === 'edit' && (
            <TextArea
              ref={textRef}
              value={editorContent}
              onChange={(e) => setEditorContent(e.target.value)}
              spellCheck={false}
            />
          )}

          {activeTab === 'preview' && (
            <PreviewArea $visible={activeTab === 'preview'}>
              <PreviewContent>
                <PreviewTitle>Preview do Script</PreviewTitle>
                <PreviewText>{testResult || editorContent}</PreviewText>
              </PreviewContent>
            </PreviewArea>
          )}

          <LoadingOverlay $visible={loading}>
            <LoadingSpinner />
          </LoadingOverlay>
        </EditorArea>
      </EditorContent>

      <StatusBar>
        <StatusItem>
          <span>Linhas: {editorContent.split('\n').length}</span>
        </StatusItem>
        <StatusItem>
          <span>Caracteres: {editorContent.length}</span>
        </StatusItem>
        <StatusItem>
          <span>Tipo: {getTypeLabel()}</span>
        </StatusItem>
      </StatusBar>
    </EditorContainer>
  );
};

export default ScriptEditor;

